// API contract shared by the Netlify Functions backend and the React frontend.

export type Role = "vet" | "owner";
export type Tone = "high" | "warn" | "ok" | "info" | "teal" | "purple";

export interface Me {
  email: string;
  role: Role;
  displayName: string;
  vet?: { id: number; name: string; clinicId: number; clinicName: string };
  ownerId?: number;
}

export interface PetSummary {
  id: number;
  patientNo: string;
  microchip: string;
  name: string;
  displayName: string;
  species: string;
  breed: string | null;
  photoUrl: string | null;
  ownerName: string | null;
  lastVisit: string | null;
  blocks: number;
  hasDrugAllergy: boolean;
}

export interface Tag { label: string; tone: Tone }

export interface Visit {
  id: number;
  visitNo: string | null;
  date: string;
  endDate: string | null;
  dateLabel: string | null;
  title: string;
  summary: string;
  notes: string;
  tags: Tag[];
  clinic: string;
  vet: string;
  blockSeq: number | null;
  vitals: { tempF: number | null; heartRate: number | null; respRate: number | null; painScore: number | null; complaint: string | null; anamnesis: string | null; recordedBy: string } | null;
  treatments: { kind: string; description: string; dose: string | null; givenBy: string }[];
}

export interface Condition { name: string; detail: string; icon: string | null; tone: Tone; status: string; since: string }

export interface VaccineItem { name: string; detail: string; due: string | null; days: number | null; given?: string }

export interface Prescription {
  id: number;
  rxNo: string | null;
  category: "medication" | "prevention" | "diet" | "topical";
  name: string;
  instructions: string;
  indication: string | null;
  icon: string | null;
  refills: number | null;
  startedOn: string;
  endsOn: string | null;
  status: "active" | "ongoing" | "completed";
  vet: string;
  firstDispensed?: string;
  lastSupplied?: string;
}

export interface LabResult {
  analyte: string;
  valueNum: number | null;
  valueText: string | null;
  unit: string | null;
  refLow: number | null;
  refHigh: number | null;
  refText: string | null;
  flag: "N" | "H" | "L" | "NEG" | "POS";
}

export interface LabPanel { id: number; name: string; category: string; collectedOn: string; labRef: string | null; results: LabResult[] }

export interface ImagingStudy { id: number; modality: string; title: string; studyDate: string; views: { label: string; sub?: string }[]; report: string }

export interface BehaviourNote { id: number; source: "clinic" | "owner"; kind: "positive" | "watch" | "note"; headline: string | null; body: string; author: string; notedOn: string }

export interface ChainBlock {
  seq: number;
  type: "visit" | "note" | "registration";
  refId: number;
  date: string;
  summary: string;
  author: string;
  clinic: string | null;
  hash: string;
  prevHash: string;
  sealedAt: string;
}

export interface ExternalMed {
  source: "owner" | "other_clinic";
  name: string;
  instructions: string;
  quantity: string | null;
  startedOn: string | null;
  endsOn: string | null;
  reportedOn: string;
}

export interface PetRecord {
  pet: {
    id: number; patientNo: string; microchip: string; name: string; displayName: string;
    species: string; breed: string | null; sex: string | null; color: string | null;
    birthDate: string | null; ageLabel: string | null; photoUrl: string | null;
    weightLb: number | null; lifestyle: Record<string, string>;
  };
  owner: { name: string; address: string | null; phone: string | null; email: string | null } | null;
  lastClinic: { name: string; address: string; phone: string | null; vet: string } | null;
  recordDate: string | null;
  allergies: { drug: string[]; food: string[]; medical: string[] };
  conditions: Condition[];
  visits: Visit[];
  vaccines: { overdue: VaccineItem[]; upcoming: VaccineItem[]; given: VaccineItem[] };
  prescriptions: { active: Prescription[]; diet: Prescription[]; past: Prescription[] };
  /** Current medication from outside the clinic (latest report per name, still running). */
  externalMeds: ExternalMed[];
  /** Date of the latest visit at which the vet reviewed (reconciled) the medication list. */
  medsReconciledOn: string | null;
  labs: LabPanel[];
  imaging: ImagingStudy[];
  weights: { date: string; lb: number }[];
  behaviour: BehaviourNote[];
  chain: ChainBlock[];
  today: string;
}

export interface VerifyResult {
  valid: boolean;
  checkedAt: string;
  blocks: { seq: number; hash: string; linkOk: boolean; hashOk: boolean; payloadOk: boolean }[];
}

export interface EmergencyCard {
  pet: PetRecord["pet"];
  allergies: PetRecord["allergies"];
  conditions: Condition[];
  medications: Prescription[];
  externalMeds: ExternalMed[];
  cautions: BehaviourNote[];
  ownerPhone: string | null;
  lastClinic: PetRecord["lastClinic"];
  verified: boolean;
}

export interface NewVisitInput {
  visitDate?: string;
  title: string;
  summary: string;
  notes?: string;
  tags?: Tag[];
  weightLb?: number;
  vaccinations?: { name: string; product?: string; batchNo?: string; nextDue?: string }[];
  prescriptions?: { category: Prescription["category"]; name: string; instructions?: string; quantity?: string; endsOn?: string }[];
  /** Medication the pet takes that the clinic did not prescribe (owner-reported or another clinic's order). */
  externalMeds?: { source: ExternalMed["source"]; name: string; instructions?: string; quantity?: string; startedOn?: string; endsOn?: string }[];
  allergies?: { category: "drug" | "food" | "medical"; substance: string }[];
  /** New conditions, or a new status / detail for an existing one (the latest entry per name wins). */
  conditions?: { name: string; detail?: string; status: ConditionStatus; icon?: string | null; tone?: Tone }[];
  /** New behaviour observations — each is sealed as its own note block right after the visit. */
  behaviour?: { kind: "positive" | "watch" | "note"; headline?: string; body: string }[];
  /** Main-page parameters the vet reviewed at this visit (see REVIEW_ITEMS). */
  reviewed?: ReviewItem[];
}

export type ConditionStatus = "active" | "suspected" | "managed" | "resolved";

/** The overview's main parameters — every visit asks the vet to touch each of them. */
export const REVIEW_ITEMS = [
  { key: "weight", label: "Weight" },
  { key: "allergies", label: "Allergies & sensitivities" },
  { key: "conditions", label: "Chronic conditions" },
  { key: "vaccinations", label: "Vaccinations" },
  { key: "prevention", label: "Medications & prevention" },
  { key: "behaviour", label: "Behavior" },
] as const;
export type ReviewItem = (typeof REVIEW_ITEMS)[number]["key"];

export interface NewNoteInput { kind: "positive" | "watch" | "note"; headline?: string; body: string }

export interface OwnerSummary { id: number; name: string; phone: string | null; email: string | null; pets: number }

export interface NewPetInput {
  microchip: string;
  name: string;
  species: "Canine" | "Feline" | "Other";
  breed?: string;
  sex?: "Male" | "Female" | "Neutered Male" | "Spayed Female";
  color?: string;
  birthDate?: string;
  /** Either an existing owner (`id`) or the details of a new one (`fullName` required). */
  owner: { id?: number; fullName?: string; phone?: string; email?: string; address?: string };
}

export interface RegisterResult { pet_id: number; patient_no: string; owner_id: number; seq: number; hash: string; prev_hash: string }
